import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClearFilter } from '../../assets/icons/filter-close.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search-icon.svg';
import { AvailableRevenuePayout } from '../../interface/partner-interfaces/transaction-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slices/NotificationSlice';
import { fetchRevenuePayout } from '../../redux/slices/Partners/TransactionManagementSlice';
import BatteryLoader from '../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../shared-components/button/Button';
import CustomDatePicker from '../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../shared-components/custom-input/CustomInput';
import ExportButton from '../../shared-components/export-button/ExportButton';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import transaction from '../../utils/api/partner/profile';

/**
 * rilo
 * @returns {any} The result
 */
function WithdrawalHistory() {
  const { t } = useTranslation('profile');
  const dispatch = useAppDispatch();
  const { revenuePayout, loading } = useAppSelector((state) => state.partnersTransaction);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    searchBy: string;
    startDate: string;
    endDate: string;
  }>({
    searchBy: '',
    startDate: '',
    endDate: '',
  });
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    transaction
      .getStripeHistoryExport({})
      .then((response) => {
        const headers = [
          'Account Id',
          'Revenue From Date',
          'Revenue To Date',
          'Payout ID',
          'Payout Amount',
          'Status',
          'Payout Date',
        ];
        const rows = response.data.results.map((row: AvailableRevenuePayout) => [
          row.accountCustomId || '',
          row.fromDate ? ConvertUTCtoDate(row.fromDate) : '',
          row.toDate ? ConvertUTCtoDate(row.toDate) : '',
          row.transferId || '',
          row.amount || '',
          row.status || '',
          row.transferredAt ? ConvertUTCtoDate(row.transferredAt) : '',
        ]);
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'offpeak-price-list.csv');
          } else {
            exportPDF(headers, rows, 'offpeak-price-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  // Handle input change
  /**
   * Handles changes to the filter input and updates the search value.
   * @param {any} name - The name of the filter.
   * @param {any} value - The value to filter by.
   */
  const handleInputChange = (name: string, value: string) => {
    const updatedFilters = { ...filters, [name]: value };
    setFilters(updatedFilters);

    if (name === 'searchBy') {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        dispatch(fetchRevenuePayout(updatedFilters));
      }, 1000);
    } else {
      // If startDate or endDate, trigger fetch immediately
      dispatch(fetchRevenuePayout(updatedFilters));
    }
  };
  /**
   * clear Filters
   */
  const handleClearFilters = () => {
    setFilters({
      searchBy: '',
      startDate: '',
      endDate: '',
    });
    dispatch(fetchRevenuePayout({}));
  };
  useEffect(() => {
    if (revenuePayout.length === 0) {
      dispatch(fetchRevenuePayout({}));
    }
  }, []);

  return (
    <div className="history-table-container">
      <div className="filter-content">
        <div>
          <MyButton
            label=""
            buttonType="none"
            onClickFunc={handleClearFilters}
            svgIcon={<ClearFilter />}
            hoverLabel={t('clearFilter')}
          />
        </div>

        <div className="date-box">
          <CustomDatePicker
            inputLabel={t('startDate')}
            value={filters.startDate}
            name="startDate"
            onChangeValue={handleInputChange}
            placeholder={t('datePlaceholder')}
          />
        </div>
        <div className="date-box">
          <CustomDatePicker
            placeholder={t('datePlaceholder')}
            inputLabel={t('endDate')}
            value={filters.endDate}
            name="endDate"
            onChangeValue={handleInputChange}
          />
        </div>
        <div className="search-box">
          {' '}
          <CustomInput
            inputLabel=""
            type="text"
            placeholder={t('searchHere')}
            name="searchBy"
            svgIcon={<SearchIcon />}
            value={filters.searchBy}
            onChangeValue={(_, value) => handleInputChange('searchBy', String(value))}
          />
        </div>
      </div>

      {loading ? (
        <div className="table-loading">
          <BatteryLoader />
        </div>
      ) : (
        <table className="withdrawal-table">
          <thead className="table-head">
            <tr>
              <th className="table-th">{t('serialNumber')}</th>
              <th className="table-th">{t('accountIDLabel')} </th>
              <th className="table-th">{t('revenueFromDate')}</th>
              <th className="table-th">{t('revenueToDate')}</th>
              <th className="table-th">{t('payoutID')}</th>
              <th className="table-th">{t('payoutAmount')}</th>
              <th className="table-th">{t('status')}</th>
              <th className="table-th">{t('payoutDate')} </th>
            </tr>
          </thead>
          <tbody className="table-body">
            {revenuePayout.map((item, index) => (
              <tr className="tr-body" key={item._id}>
                <td className="td-body">{index + 1}</td>
                <td className="td-body">{item.accountCustomId}</td>
                <td className="td-body">{ConvertUTCtoDate(item.fromDate)}</td>
                <td className="td-body">{ConvertUTCtoDate(item.toDate)}</td>
                <td className="td-body">{item.transferId}</td>
                <td className="td-body">₹{item.amount}</td>
                <td className="td-body">{item.status}</td>
                <td className="td-body">{ConvertUTCtoDate(item.transferredAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="export-data">
        <ExportButton
          label={t('exportData')}
          buttonList={[
            {
              key: 'pdf',
              allow: true,
              /**
               * Export pdf function
               * @returns {any} The result
               */
              onClickFunction: () => exportDataPdfOrCSV('pdf'),
            },
            {
              key: 'csv',
              allow: true,
              /**
               * Export csv function
               * @returns {any} The result
               */
              onClickFunction: () => exportDataPdfOrCSV('csv'),
            },
          ]}
          disabled={revenuePayout.length === 0}
          loader={exportLoader}
        />
      </div>
    </div>
  );
}

export default WithdrawalHistory;
